// WCAG 2.2.2 (Pause, Stop, Hide) for the marketing surface — MARKETING_10X_PLAN P1-6.
// The marketing pages run several >5s auto-motions (badge float, aura pulse, drifting glows,
// the Aira ticker); honoring prefers-reduced-motion alone is NOT sufficient — users must be able
// to pause from the page itself. `MarketingMotion` provides that switch, and the shared footer
// exposes it through `MotionPauseButton` on every marketing route:
//   • framer-motion: MotionConfig reducedMotion="always" suppresses transform/layout motion in
//     the subtree. JavaScript timers and requestAnimationFrame demos must additionally combine
//     `useReducedMotion()` with `useMarketingMotion().paused`.
//
// It is ALSO the marketing surface's `LazyMotion` boundary (PERF-6). `motion.*` statically bundles
// framer-motion's ENTIRE feature set — including drag and the layout-projection engine — into
// whichever chunk imports it, and the landing route is the eager entry chunk, so the whole set was
// shipped on first paint. `m.*` is the tree-shakeable component set and `domAnimation` (animation +
// exit + inView + hover/focus/tap) is the only feature bundle the marketing pages actually use.
// `strict` is the guard-rail: in development any surviving `motion.*` rendered below this provider
// throws with "render an `m` component instead", so the eager chunk cannot silently regain the full
// bundle. Two deliberate exceptions mount their OWN nested LazyMotion because they also render
// outside this provider or need a bigger bundle — `components/AiraAssistant` (mounted standalone by
// App's MarketingAira gate) and `landingparts/AiSuiteSection` (`layoutId`, so it needs `domMax`).
// AnimatePresence still comes from framer-motion directly; `useReducedMotion`/`MotionConfig` are
// unaffected by the split.
//   • CSS keyframe loops: a `data-marketing-motion="paused"` attribute on <html> drives an
//     animation-play-state override scoped to `.landing-page` containers (index.css).
// The choice persists per-device (localStorage) and applies across every marketing page.
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { LazyMotion, MotionConfig, domAnimation } from "framer-motion";
import { Pause, Play } from "lucide-react";

const STORAGE_KEY = "vp-marketing-motion"; // "paused" | anything else = running
const Ctx = createContext({ paused: false, toggle: () => {} });

export function useMarketingMotion() {
  return useContext(Ctx);
}

export function MarketingMotion({ children }) {
  const [paused, setPaused] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "paused"; } catch { return false; }
  });
  useEffect(() => {
    const root = document.documentElement;
    if (paused) root.setAttribute("data-marketing-motion", "paused");
    else root.removeAttribute("data-marketing-motion");
    return () => root.removeAttribute("data-marketing-motion");
  }, [paused]);
  const toggle = useCallback(() => {
    setPaused((p) => {
      const next = !p;
      try { localStorage.setItem(STORAGE_KEY, next ? "paused" : "running"); } catch { /* private mode */ }
      return next;
    });
  }, []);
  return (
    <Ctx.Provider value={{ paused, toggle }}>
      <MotionConfig reducedMotion={paused ? "always" : "user"}>
        <LazyMotion features={domAnimation} strict>{children}</LazyMotion>
      </MotionConfig>
    </Ctx.Provider>
  );
}

export function MotionPauseButton() {
  const { paused, toggle } = useMarketingMotion();
  const Icon = paused ? Play : Pause;
  return (
    <button type="button" onClick={toggle} aria-pressed={paused}
      className="inline-flex min-h-11 items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-2 text-sm font-medium text-white/70 transition-[background-color,border-color,color,transform] duration-200 hover:border-white/30 hover:bg-white/[0.1] hover:text-white active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky">
      <Icon className="h-4 w-4" aria-hidden="true" />
      {paused ? "Resume animations" : "Pause animations"}
    </button>
  );
}
